import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { getReportSchedule, putReportSchedule, type ReportFrequency } from '../api/reports';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { useT } from '../i18n';

// Admin-only блок настройки планового отчёта (ANX-068). PUT заменяет весь
// конфиг целиком → редактируем локальный draft и сохраняем кнопкой.

function parseRecipients(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function ScheduledReportsSection() {
  const t = useT();
  const operator = useAuthStore((s) => s.session?.operator);
  const qc = useQueryClient();
  const isAdmin = operator?.role === 'admin';
  const [error, setError] = useState<string | null>(null);

  const scheduleQuery = useQuery({
    queryKey: ['report_schedule'],
    queryFn: () => getReportSchedule(getApiClient()),
    enabled: isAdmin,
  });

  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState<ReportFrequency>('weekly');
  const [recipientsText, setRecipientsText] = useState('');
  const [timezone, setTimezone] = useState('UTC');

  // Синхронизируем draft с загруженной конфигурацией.
  const loaded = scheduleQuery.data;
  useEffect(() => {
    if (!loaded) return;
    setEnabled(loaded.enabled);
    setFrequency(loaded.frequency);
    setRecipientsText(loaded.recipients.join('\n'));
    setTimezone(loaded.timezone || browserTimezone());
  }, [loaded]);

  const mu = useMutation({
    mutationFn: () =>
      putReportSchedule(getApiClient(), {
        enabled,
        frequency,
        recipients: parseRecipients(recipientsText),
        timezone: timezone.trim() || 'UTC',
      }),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['report_schedule'] });
      qc.invalidateQueries({ queryKey: ['report_runs'] });
    },
    onError: () => setError(t('reports.schedule.saveError')),
  });

  if (!isAdmin) return null;

  const nextRun = loaded?.nextRunAt;
  const busy = mu.isPending || scheduleQuery.isLoading;

  return (
    <section className="mt-4 rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <div className="mb-2 flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-accent-600" />
        <h3 className="text-sm font-medium text-slate-900">{t('reports.schedule.heading')}</h3>
      </div>
      <p className="mb-3 text-xs text-slate-500">{t('reports.schedule.hint')}</p>

      <label className="flex items-start gap-2.5 text-sm text-slate-800">
        <input
          type="checkbox"
          checked={enabled}
          disabled={busy}
          onChange={(e) => setEnabled(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-accent-600 focus:ring-accent-500"
        />
        <span className="font-medium">{t('reports.schedule.enabled')}</span>
      </label>

      <div className="mt-3">
        <label className="block text-sm text-slate-800">
          <span className="font-medium">{t('reports.schedule.frequency')}</span>
          <select
            value={frequency}
            disabled={busy || !enabled}
            onChange={(e) => setFrequency(e.target.value as ReportFrequency)}
            className="mt-1 block w-56 rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500"
          >
            <option value="daily">{t('reports.frequency.daily')}</option>
            <option value="weekly">{t('reports.frequency.weekly')}</option>
            <option value="monthly">{t('reports.frequency.monthly')}</option>
          </select>
        </label>
      </div>

      <div className="mt-3">
        <label className="block text-sm text-slate-800">
          <span className="font-medium">{t('reports.schedule.recipients')}</span>
          <textarea
            value={recipientsText}
            disabled={busy || !enabled}
            onChange={(e) => setRecipientsText(e.target.value)}
            rows={3}
            placeholder={t('reports.schedule.recipientsPlaceholder')}
            className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500"
          />
          <span className="mt-1 block text-xs text-slate-500">
            {t('reports.schedule.recipientsHint')}
          </span>
        </label>
      </div>

      <div className="mt-3">
        <label className="block text-sm text-slate-800">
          <span className="font-medium">{t('reports.schedule.timezone')}</span>
          <input
            type="text"
            value={timezone}
            disabled={busy || !enabled}
            onChange={(e) => setTimezone(e.target.value)}
            className="mt-1 block w-56 rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500"
          />
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => mu.mutate()}
          className="rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
        >
          {mu.isPending ? t('reports.schedule.saving') : t('common.save')}
        </button>
        {enabled && nextRun && (
          <span className="text-xs text-slate-500">
            {t('reports.schedule.nextRun')}: {new Date(nextRun).toLocaleString()}
          </span>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}
